import React, { useContext, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, User, TrendingUp, Activity, FileText } from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import adminStatsService from '../../../../services/adminStatsService';
import ActivityTrendsChart from './ActivityTrendsChart';
import RiskDistributionChart from './RiskDistributionChart';
import RecentHistoriesSection from './RecentHistoriesSection';


export default function AdminDashboardHome() {
  const { authTokens } = useContext(AuthContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [activityTrends, setActivityTrends] = useState(null);
  const [riskDistribution, setRiskDistribution] = useState(null);
  const [recentHistories, setRecentHistories] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!authTokens.access) {
        console.log('❌ No access token available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('🚀 Starting admin dashboard data fetch...');

        // Fetch all data in parallel
        const [statsResult, trendsResult, distributionResult, historiesResult] = await Promise.all([
          adminStatsService.getDashboardStats(authTokens.access),
          adminStatsService.getActivityTrends(authTokens.access),
          adminStatsService.getRiskDistribution(authTokens.access),
          adminStatsService.getRecentHistories(authTokens.access)
        ]);

        if (statsResult.success) {
          setDashboardData(statsResult.data);
          console.log('✅ Dashboard stats loaded:', statsResult.data);
        }

        if (trendsResult.success) {
          setActivityTrends(trendsResult.data);
          console.log('✅ Activity trends loaded:', trendsResult.data);
        }

        if (distributionResult.success) {
          setRiskDistribution(distributionResult.data);
          console.log('✅ Risk distribution loaded:', distributionResult.data);
        }


        if (historiesResult.success) {
          setRecentHistories(historiesResult.data);
          console.log('✅ Recent histories loaded:', historiesResult.data);
        }

        console.log('✅ All API data loaded successfully!');
      } catch (error) {
        console.error('❌ Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [authTokens.access]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData || {};
  const riskStatus = stats.risk_status || {};
  const processHealth = stats.process_health || {};
  const systemActivity = stats.system_activity || {};
  const teamOverview = stats.team_overview || {};
  const auditOverview = stats.audit_overview || {};
return (
    <div className="space-y-6">
      {/* Welcome Message */}
    <div className="md:flex justify-between items-center bg-green-50 border border-green-200 rounded-md p-2">
      <h2 className="text-base font-semibold text-gray-800">
        Bienvenue sur Cartopousse 👋
      </h2>
      <p className="text-gray-600 text-sm">
        Gérez vos risques et cartographies facilement.
      </p>
    </div>


      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Risks */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Risques totaux</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{riskStatus.total_risks || 0}</p>
              <div className="mt-2 flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  {riskStatus.pending || 0} en attente
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        {/* Total Processes */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Processus totaux</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{processHealth.total_processes || 0}</p>
              <div className="mt-2 flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  {processHealth.published_processes || 0} publiés
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="h-6 w-6 text-black" />
            </div>
          </div>
        </div>

        {/* Active Users */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Utilisateurs actifs</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{teamOverview.active_users || 0}</p>
              <div className="mt-2 flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                  {teamOverview.new_users_this_month || 0} ce mois
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Tasks in Progress */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Tâches en cours</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{systemActivity.tasks_in_progress || 0}</p>
              <div className="mt-2 flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  {systemActivity.tasks_completed || 0} terminées
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Activity className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Risk Actions */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">Actions sur les risques</h4>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">En attente</span>
              <span className="text-xs font-semibold text-gray-900">{riskStatus.pending || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Approuvés</span>
              <span className="text-xs font-semibold text-green-600">{riskStatus.approved || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Rejetés</span>
              <span className="text-xs font-semibold text-red-600">{riskStatus.rejected || 0}</span>
            </div>
          </div>
        </div>

        {/* Process Health */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">État des processus</h4>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Brouillons</span>
              <span className="text-xs font-semibold text-gray-900">{processHealth.draft_processes || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Publiés</span>
              <span className="text-xs font-semibold text-green-600">{processHealth.published_processes || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Accomplis</span>
              <span className="text-xs font-semibold text-blue-600">{processHealth.achieved_processes || 0}</span>
            </div>
          </div>
        </div>

        {/* System Activity */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">Activité système</h4>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Risques créés</span>
              <span className="text-xs font-semibold text-gray-900">{systemActivity.risks_created || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Processus créés</span>
              <span className="text-xs font-semibold text-gray-900">{systemActivity.processes_created || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Tâches terminées</span>
              <span className="text-xs font-semibold text-gray-900">{systemActivity.tasks_completed || 0}</span>
            </div>
          </div>
        </div>

        {/* Audit Overview */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">Aperçu des audits</h4>
            <CheckCircle className="h-4 w-4 text-blue-500" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Programmes d'audit</span>
              <span className="text-xs font-semibold text-gray-900">{auditOverview.total_audit_programs || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Plans d'audit</span>
              <span className="text-xs font-semibold text-gray-900">{auditOverview.total_audit_plans || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Actions de recommandation</span>
              <span className="text-xs font-semibold text-gray-900">{auditOverview.total_recommendation_actions || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Activity Trends Chart */}
        <ActivityTrendsChart data={activityTrends} />

        {/* Risk Distribution Chart */}
        <RiskDistributionChart data={riskDistribution} />
      </div>



    {/* Recent Histories Section */}
    <RecentHistoriesSection data={recentHistories} loading={loading} />   
    </div>
  );
}